import React, { memo, useMemo, useCallback, useEffect, useState } from 'react';
import { formGroup, formControl, FormDataType, formArray } from '@core/utils/form';
import { defineBlockAdvanceForm } from '../Common/AdvanceSettings';
import { useNonNilObservable, useObservable } from '@core/utils/hooks/rxjs';
import { useConfigureBlock, useCourseForm } from '@modules/course/CourseBuilder/hooks';
import { ProcessBlockSettings } from './ProcessBlockSettings';
import { Container } from 'reactstrap';
import { of } from 'rxjs';
import { useBloc } from '@core/utils/bloc';
import { ProcessBlockBloc } from './ProcessBlockBloc';
import { Button } from '@core/components/Button';
import { CourseBuilder } from '../types';
import { BlockContainer } from '../BlockContainer';
import { R } from '@core/utils/r';
import { useDidMount } from '@core/utils/hooks/react';
import { Card, CardBody } from 'reactstrap';
import { HTMLService } from '@business/services';
import './index.scss';
import { SimplePagination } from '@core/components/SimplePagination';

function defineStepForm() {
  return formGroup({
    stepTitle: formControl<string>({
      placeholder: 'Step Title',
      required: true,
      initialValue: 'Step Title',
    }),
    stepDetails: formControl<string>({
      initialValue:
        '<div>Quam nisi convallis imperdiet porta venenatis ultricies litora netus in pellentesque vulputate laoreet, integer varius metus velit conubia leo purus egestas curabitur cubilia.</div><div><img src="https://images.unsplash.com/photo-1541584285245-c83a93cce0e8?crop=entropy&amp;cs=tinysrgb&amp;fit=max&amp;fm=jpg&amp;ixid=Mnw0MTYxMTh8MHwxfHNlYXJjaHwxMnx8YmljeWNsZXxlbnwwfHx8fDE2NzkzMzY2NDQ&amp;ixlib=rb-4.0.3&amp;q=80&amp;w=500" alt="" width="300px"></div>',
    }),
  });
}

function defineProcessBlockForm() {
  return formGroup({
    processTitle: formControl<string>({
      placeholder: 'Process Title',
      required: false,
      initialValue: 'Process',
    }),
    introTitle: formControl<string>({
      placeholder: 'Intro Title',
      required: true,
      initialValue: 'Intro Title',
    }),
    introDetails: formControl<string>({
      initialValue:
        'Quam nisi convallis imperdiet porta venenatis ultricies litora netus in pellentesque vulputate laoreet, integer varius metus velit conubia leo purus egestas curabitur cubilia.',
    }),
    steps: formArray(() => defineStepForm()),
    summaryTitle: formControl<string>({
      placeholder: 'Summary Title',
      required: true,
      initialValue: 'Summary Title',
    }),
    summaryDetails: formControl<string>({
      initialValue:
        'Quam nisi convallis imperdiet porta venenatis ultricies litora netus in pellentesque vulputate laoreet, integer varius metus velit conubia leo purus egestas curabitur cubilia.',
    }),
    advance: defineBlockAdvanceForm({ paddingBottom: 30, paddingTop: 30 }),
  });
}

export type ProcessForm = ReturnType<typeof defineProcessBlockForm>;
export type ProcessData = FormDataType<typeof defineProcessBlockForm>;
export type StepForm = ReturnType<typeof defineStepForm>;

export enum ProcessBlockVariation {
  process = 'process',
}

export const ProcessBlock = memo((props: CourseBuilder.BlockProps<ProcessBlockVariation>) => {
  const { variation, preview, lessonId } = props;
  const form = useMemo(() => defineProcessBlockForm(), []);
  const formValue = useNonNilObservable(form.value$);
  const bloc = useBloc(ProcessBlockBloc, lessonId);

  const steps = useObservable(form.controls.steps.items$, []);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [begin, setBegin] = useState(false);
  const activeStep = useMemo(() => R.get(steps, activeIndex), [activeIndex, steps]);
  const isProcessCompleted = useMemo(() => activeIndex === steps.length, [activeIndex, steps.length]);

  useDidMount(() => {
    if (props.preview && !formValue.steps.length) {
      form.patchValue({
        introTitle: 'Title',
        introDetails: 'Description intro',
        steps: [
          {
            stepTitle: 'Step 1',
            stepDetails:
              '<div>Quam nisi convallis imperdiet porta venenatis ultricies litora netus in pellentesque vulputate laoreet, integer varius metus velit conubia leo purus egestas curabitur cubilia.</div><div><img src="https://images.unsplash.com/photo-1541584285245-c83a93cce0e8?crop=entropy&amp;cs=tinysrgb&amp;fit=max&amp;fm=jpg&amp;ixid=Mnw0MTYxMTh8MHwxfHNlYXJjaHwxMnx8YmljeWNsZXxlbnwwfHx8fDE2NzkzMzY2NDQ&amp;ixlib=rb-4.0.3&amp;q=80&amp;w=500" alt="" width="300px"></div>',
          },
        ],
        summaryTitle: 'Summary',
        summaryDetails: 'Summary desccc',
      });
    }
  });

  const onResetProcess = useCallback(() => {
    setActiveIndex(0);
    setBegin(false);
  }, []);

  useEffect(() => {
    if (activeIndex === -1 && steps.length > 0) {
      setActiveIndex(0);
    }
  }, [activeIndex, steps.length]);

  const onSubmit = useCallback(() => of(isProcessCompleted), [isProcessCompleted]);

  useCourseForm(props.id, form, { onSubmit });

  const onNext = useCallback(
    (shouldMove: boolean) => {
      if (!activeStep) return;
      if (props.editable || props.preview) return shouldMove && setActiveIndex(activeIndex + 1);
      setActiveIndex(activeIndex + 1);
    },
    [activeIndex, activeStep, props.editable, props.preview],
  );

  const onPrevious = useCallback(
    (shouldMove: boolean) => {
      if (!activeStep) return;
      if (props.editable || props.preview) return shouldMove && setActiveIndex(activeIndex - 1);
      setActiveIndex(activeIndex - 1);
    },
    [activeIndex, activeStep, props.editable, props.preview],
  );

  const startSteps = useCallback(() => {
    setBegin(true);
  }, []);

  useConfigureBlock({
    props,
    form,
    contentComponent: ProcessBlockSettings,
    contentProps: { form, variation },
  });

  const onPage = useCallback((page: number) => {
    setActiveIndex(page - 1);
  }, []);

  return (
    <Container>
      <BlockContainer advanceForm={formValue.advance}>
        {!begin && (
          <Card className="mt-4 text-center">
            <CardBody>
              <h1 className="mb-3">{HTMLService.toReact(form.controls.introTitle.value || '')}</h1>
              <p>{HTMLService.toReact(form.controls.introDetails.value || '')}</p>
              <Button type="button" onClick={startSteps} disabled={formValue.steps.length == 0 ? true : false}>
                Start <i className="bx bx-chevron-right" />
              </Button>
            </CardBody>
          </Card>
        )}
        {!!activeStep && begin && (
          <ScenarioItem
            form={activeStep}
            onNext={onNext}
            onPrevious={onPrevious}
            onResetProcess={onResetProcess}
            preview={!!props.preview}
            index={activeIndex + 1}
            totalSteps={steps.length}
            onPage={onPage}
            title={formValue.processTitle}
          />
        )}
        {isProcessCompleted && (
          <Card className="mt-4 text-center">
            <CardBody>
              <h1 className="mb-3">{HTMLService.toReact(form.controls.summaryTitle.value || '')}</h1>
              <p>{HTMLService.toReact(form.controls.summaryDetails.value || '')}</p>
              <Button type="button" onClick={onResetProcess}>
                Start Again <i className="bx bx-redo" />
              </Button>
            </CardBody>
          </Card>
        )}
      </BlockContainer>
    </Container>
  );
});

interface StepItemProps {
  form: StepForm;
  onNext: (val: boolean) => void;
  onPrevious: (val: boolean) => void;
  onResetProcess: () => void;
  preview: boolean;
  totalSteps: number;
  index: number;
  onPage?(page: number): void;
  title?: string;
}

const ScenarioItem = memo((props: StepItemProps) => {
  const { form, preview, index, totalSteps, onPage, title } = props;
  const formValue = useNonNilObservable(form.value$);

  const onNext = useCallback(() => {
    props.onNext(true);
  }, [props]);

  const onPrevious = useCallback(() => {
    props.onPrevious(true);
  }, [props]);

  const onResetProcess = useCallback(() => {
    props.onResetProcess();
  }, [props]);

  return (
    <div style={{ alignItems: 'center', display: 'flex', flexDirection: 'column' }}>
      {!!title && (
        <span className="btn btn-primary" style={{ marginBottom: -25 }}>
          {title} {index}
        </span>
      )}
      <div style={{ display: 'flex', justifyContent: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <div style={{ width: 40, marginRight: 10 }}>
            <Button
              intent="primary"
              style={{ borderRadius: 40, padding: 0, width: 40, height: 40 }}
              type="button"
              onClick={() => {
                index === 1 ? onResetProcess() : onPrevious();
              }}
            >
              <i className="bx bx-chevron-left font-size-20" />
            </Button>
          </div>
          <Card className="mt-4 text-center">
            <CardBody>
              <h1 className="mb-3">{HTMLService.toReact(formValue.stepTitle)}</h1>
              <p className="mt-2">{HTMLService.toReact(formValue.stepDetails)}</p>
              <SimplePagination total={totalSteps} page={index} onPage={onPage} />
            </CardBody>
          </Card>
          <div style={{ width: 40, marginLeft: 5 }}>
            <Button type="button" intent="primary" style={{ borderRadius: 40, padding: 0, width: 40, height: 40 }} onClick={onNext}>
              <i className="bx bx-chevron-right font-size-20" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
});
